import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface StartAuctionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (endTime: Date) => void;
  loading?: boolean;
}

export const StartAuctionDialog = ({ open, onOpenChange, onConfirm, loading }: StartAuctionDialogProps) => {
  const [endDate, setEndDate] = useState<Date>();
  const [endTime, setEndTime] = useState("17:00");

  const handleConfirm = () => {
    if (!endDate) return;

    const [hours, minutes] = endTime.split(":").map(Number);
    const combinedDateTime = new Date(endDate);
    combinedDateTime.setHours(hours, minutes, 0, 0);

    // Validate that end time is in the future
    if (combinedDateTime <= new Date()) {
      return;
    }

    onConfirm(combinedDateTime);
  };

  const isValidEndTime = endDate && (() => {
    const [hours, minutes] = endTime.split(":").map(Number);
    const combinedDateTime = new Date(endDate);
    combinedDateTime.setHours(hours, minutes, 0, 0);
    return combinedDateTime > new Date();
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Start Reverse Auction</DialogTitle>
          <DialogDescription>
            Set when you want the auction to end. The auction will start immediately.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="end-date">Auction End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="end-date"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !endDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-time">Auction End Time</Label>
            <Input
              id="end-time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          {endDate && !isValidEndTime && (
            <p className="text-sm text-destructive">
              End time must be in the future
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!isValidEndTime || loading}
          >
            {loading ? "Starting..." : "Start Auction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
